import React from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import {
  Users, FileText, Inbox, Package, AlertTriangle, CreditCard, BarChart3,
  Settings, Bot, Link2, Zap, QrCode, FileBarChart, Megaphone, Brain,
  Repeat, Building2, Archive, ChevronRight, Wallet, Landmark, HardHat, Briefcase,
  CalendarCheck, ListChecks, CalendarDays, LayoutDashboard, FolderOpen, Search, X, CornerDownRight,
  ClipboardCheck, ClipboardList,
} from 'lucide-react';

// iter 178 — iOS Settings-style sidebar. iter 214 — Extracted from Layout.js.
// iter 311 — Menü-felülvizsgálat: 6 csoport → 23 pont, ritka pontok (Tartozás-szabályok,
//            Csomagok, Lomtár, Útmutató) a Beállításokba költöztek; Nyilvántartó az Ügyfelek
//            almenüje; Automatizálás + Marketing az Elemzéshez; kereső a sidebar tetején;
//            Beállítások önálló pont alul.

const standaloneItems = [
  { path: '/', icon: LayoutDashboard, label: 'nav.dashboard' },
];

export const navGroups = [
  {
    id: 'clients',
    icon: Users,
    label: 'Ügyfelek & Munkák',
    children: [
      { path: '/clients', icon: Users, label: 'nav.clients' },
      { path: '/registry', icon: Building2, label: 'nav.registry', sub: true },
      { path: '/projects', icon: Briefcase, label: 'nav.projects' },
      { path: '/igenyek', icon: Inbox, label: 'nav.projectRequests', counter: 'requests', sub: true },
      { path: '/proposals', icon: FileBarChart, label: 'nav.proposals' },
      { path: '/orders', icon: Inbox, label: 'nav.orders', counter: 'orders' },
      { path: '/price-list', icon: ListChecks, label: 'nav.priceList' },
      { path: '/products', icon: Package, label: 'nav.products' },
    ],
  },
  {
    id: 'sales',
    icon: FileText,
    label: 'Számlázás',
    children: [
      { path: '/invoices', icon: FileText, label: 'nav.invoices' },
      { path: '/incoming-invoices', icon: Inbox, label: 'nav.incomingInvoices' },
      { path: '/recurring-invoices', icon: Repeat, label: 'nav.recurringInvoices' },
      { path: '/qr-payments', icon: QrCode, label: 'nav.qrPayments', badge: 'BETA' },
      { path: '/billing', icon: Link2, label: 'nav.billing' },
    ],
  },
  {
    id: 'finance',
    icon: Wallet,
    label: 'Pénzügy',
    children: [
      { path: '/bank', icon: Landmark, label: 'nav.bankReconciliation' },
      { path: '/debts', icon: AlertTriangle, label: 'nav.debts' },
      { path: '/payments', icon: CreditCard, label: 'nav.payments' },
    ],
  },
  {
    id: 'team',
    icon: HardHat,
    label: 'Csapat',
    children: [
      { path: '/workers', icon: HardHat, label: 'nav.workers' },
      { path: '/attendance', icon: CalendarCheck, label: 'nav.attendance' },
      { path: '/calendar', icon: CalendarDays, label: 'nav.calendar' },
    ],
  },
  {
    id: 'documents',
    icon: FolderOpen,
    label: 'Dokumentumok',
    children: [
      { path: '/documents', icon: FolderOpen, label: 'nav.documents' },
      { path: '/defect-reports', icon: ClipboardCheck, label: 'nav.defectReports' },
      { path: '/jegyzokonyvek', icon: ClipboardCheck, label: 'nav.protocols' },
      { path: '/felmero', icon: ClipboardList, label: 'nav.surveys' },
      { path: '/document-archive', icon: Archive, label: 'nav.documentArchive' },
    ],
  },
  {
    id: 'insights',
    icon: BarChart3,
    label: 'Elemzés & Automatizálás',
    children: [
      { path: '/reports', icon: BarChart3, label: 'nav.reports' },
      { path: '/predictions', icon: Brain, label: 'nav.predictions' },
      { path: '/ai-chat', icon: Bot, label: 'nav.aiChat' },
      { path: '/automation', icon: Zap, label: 'nav.automation' },
      { path: '/marketing', icon: Megaphone, label: 'nav.marketing' },
    ],
  },
];

const bottomItems = [
  { path: '/settings', icon: Settings, label: 'nav.settings' },
];

// A Beállításokba költöztetett oldalak — a kereső ezeket is megtalálja.
const movedToSettings = [
  { path: '/debt-rules', label: 'nav.debtRules' },
  { path: '/csomagok', label: 'nav.pricing' },
  { path: '/trash', label: 'nav.trash' },
  { path: '/guide', label: 'nav.guide' },
];

const COPPER = '#B85A26';
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function NavRow({ child, active, t, counters = {} }) {
  const ChildIcon = child.icon;
  const count = child.counter ? counters[child.counter] : 0;
  return (
    <Link
      to={child.path}
      data-testid={`nav-${child.path.replace('/', '') || 'dashboard'}`}
      className={`group/row flex items-center gap-2.5 px-3 h-10 rounded-xl transition-colors relative ${child.sub ? 'ml-5' : ''}`}
      style={{
        color: active ? 'var(--sidebar-active-text, #0F172A)' : 'var(--sidebar-text)',
        backgroundColor: active ? 'var(--sidebar-active-bg)' : 'transparent',
        fontWeight: active ? 600 : 500,
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'var(--sidebar-overlay)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {active && <span aria-hidden="true" className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-r-full" style={{ backgroundColor: COPPER }} />}
      {child.sub && <CornerDownRight size={12} style={{ color: 'var(--sidebar-text)', opacity: 0.4, flexShrink: 0 }} />}
      {ChildIcon && <ChildIcon size={child.sub ? 15 : 17} strokeWidth={active ? 2.2 : 1.8} style={{ color: active ? COPPER : 'var(--sidebar-text)', opacity: active ? 1 : 0.75, flexShrink: 0 }} />}
      <span className="flex-1 truncate" style={{ fontSize: child.sub ? '13px' : '13.5px', letterSpacing: '-0.01em' }}>{t(child.label)}</span>
      {child.badge && (
        <span className="font-bold px-1.5 py-0.5 rounded-full tracking-wider" style={{ fontSize: '9px', color: COPPER, backgroundColor: 'rgba(184,90,38,0.12)' }}>{child.badge}</span>
      )}
      {count > 0 && (
        <span className="min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center" style={{ backgroundColor: COPPER }} data-testid={`nav-counter-${child.counter}`}>{count > 99 ? '99+' : count}</span>
      )}
      {child.hint && <span style={{ fontSize: '10px', color: 'var(--sidebar-text)', opacity: 0.5 }}>{child.hint}</span>}
    </Link>
  );
}

// iter 312 — élő számlálók (várakozó megrendelések) a menüben
function useNavCounters() {
  const [counters, setCounters] = React.useState({});
  React.useEffect(() => {
    let alive = true;
    const load = () => {
      axios.get(`${API_URL}/api/orders/count`, { withCredentials: true }).then((r) => { if (alive) setCounters((c) => ({ ...c, orders: r.data.pending || 0 })); }).catch(() => {});
      axios.get(`${API_URL}/api/project-requests/count`, { withCredentials: true }).then((r) => { if (alive) setCounters((c) => ({ ...c, requests: r.data.pending || 0 })); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 120000);
    const onRefresh = () => load();
    window.addEventListener('orders-changed', onRefresh);
    window.addEventListener('requests-changed', onRefresh);
    return () => { alive = false; clearInterval(id); window.removeEventListener('orders-changed', onRefresh); window.removeEventListener('requests-changed', onRefresh); };
  }, []);
  return counters;
}

export default function SidebarNav({ location, t }) {
  const [query, setQuery] = React.useState('');
  const counters = useNavCounters();
  const filteredGroups = navGroups;

  const activeGroupId = React.useMemo(() => {
    for (const g of filteredGroups) {
      if (g.children.some(c => c.path === location.pathname)) return g.id;
    }
    return filteredGroups[0]?.id;
  }, [location.pathname, filteredGroups]);

  const [manualOverride, setManualOverride] = React.useState({});
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem('acountix-sidebar-groups');
      if (raw) setManualOverride(JSON.parse(raw));
    } catch { /* */ }
  }, []);

  const isGroupOpen = (groupId) => {
    if (manualOverride[groupId] !== undefined) return manualOverride[groupId];
    return groupId === activeGroupId;
  };

  const toggleGroup = (groupId) => {
    const next = { ...manualOverride, [groupId]: !isGroupOpen(groupId) };
    setManualOverride(next);
    try { localStorage.setItem('acountix-sidebar-groups', JSON.stringify(next)); } catch { /* */ }
  };

  const q = norm(query.trim());
  const searchResults = React.useMemo(() => {
    if (!q) return null;
    const all = [
      ...standaloneItems,
      ...filteredGroups.flatMap(g => g.children.map(c => ({ ...c, group: g.label }))),
      ...bottomItems,
      ...movedToSettings.map(c => ({ ...c, icon: Settings, hint: 'Beállítások' })),
    ];
    return all.filter(c => norm(t(c.label)).includes(q) || norm(c.group).includes(q));
  }, [q, filteredGroups, t]);

  return (
    <nav className="flex-1 px-3 py-3 overflow-y-auto flex flex-col" data-testid="sidebar-nav-grouped">
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--sidebar-text)', opacity: 0.5 }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('nav.searchMenu', 'Menü keresése…')}
          data-testid="sidebar-search"
          className="w-full h-9 pl-8 pr-8 rounded-xl text-[13px] outline-none focus:ring-2"
          style={{ backgroundColor: 'var(--sidebar-overlay)', color: 'var(--sidebar-text)', border: '1px solid transparent', '--tw-ring-color': 'rgba(184,90,38,0.35)' }}
        />
        {query && (
          <button type="button" onClick={() => setQuery('')} aria-label="Keresés törlése" data-testid="sidebar-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md" style={{ color: 'var(--sidebar-text)', opacity: 0.6 }}>
            <X size={13} />
          </button>
        )}
      </div>

      {searchResults ? (
        <ul className="space-y-0.5" data-testid="sidebar-search-results">
          {searchResults.length === 0 && (
            <li className="px-3 py-6 text-center text-xs" style={{ color: 'var(--sidebar-text)', opacity: 0.6 }} data-testid="sidebar-search-empty">Nincs ilyen menüpont</li>
          )}
          {searchResults.map((child) => (
            <li key={child.path}><NavRow child={{ ...child, sub: false }} active={location.pathname === child.path} t={t} counters={counters} /></li>
          ))}
        </ul>
      ) : (
        <>
          <ul className="mb-3 space-y-0.5">
            {standaloneItems.map((child) => (
              <li key={child.path}><NavRow child={child} active={location.pathname === child.path} t={t} counters={counters} /></li>
            ))}
          </ul>

          <ul className="space-y-2.5">
            {filteredGroups.map((group) => {
              const open = isGroupOpen(group.id);
              const containsActive = group.children.some(c => c.path === location.pathname);
              return (
                <li key={group.id} data-testid={`nav-group-${group.id}`}>
                  <button
                    onClick={() => toggleGroup(group.id)}
                    data-testid={`nav-group-toggle-${group.id}`}
                    className="w-full flex items-center gap-2 px-3 h-8 rounded-lg group/header hover:bg-[var(--sidebar-overlay)] transition-colors"
                  >
                    <span className="flex-1 text-left font-bold uppercase"
                      style={{ fontSize: '10.5px', letterSpacing: '0.1em', color: containsActive ? COPPER : 'var(--sidebar-text)', opacity: containsActive ? 1 : 0.7 }}>
                      {group.label}
                    </span>
                    <span className="font-semibold tabular-nums" style={{ fontSize: '11px', color: 'var(--sidebar-text)', opacity: 0.55 }}>{group.children.length}</span>
                    <ChevronRight size={14} strokeWidth={2} className="transition-transform duration-300"
                      style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', opacity: 0.6, color: containsActive ? COPPER : 'var(--sidebar-text)' }} />
                  </button>
                  <div style={{
                    maxHeight: open ? `${group.children.length * 44 + 8}px` : '0px', opacity: open ? 1 : 0, overflow: 'hidden',
                    transition: 'max-height 320ms cubic-bezier(0.32, 0.72, 0, 1), opacity 220ms ease-out',
                  }}>
                    <ul className="space-y-0.5 pt-0.5">
                      {group.children.map((child) => (
                        <li key={child.path}><NavRow child={child} active={location.pathname === child.path} t={t} counters={counters} /></li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
            })}
          </ul>

          <ul className="mt-4 pt-3 space-y-0.5 border-t" style={{ borderColor: 'var(--sidebar-overlay)' }}>
            {bottomItems.map((child) => (
              <li key={child.path}><NavRow child={child} active={location.pathname === child.path || movedToSettings.some(m => m.path === location.pathname)} t={t} /></li>
            ))}
          </ul>
        </>
      )}
    </nav>
  );
}
